import React, { useState, useEffect } from "react";
import { FaUser, FaCloudUploadAlt, FaTimes, FaSave } from "react-icons/fa";
import imageCompression from "browser-image-compression";
import slugify from "slugify";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import CommonLayout from '../../../layouts/CommonLayout';
import "./AddArtist.css";

export default function AddArtistPage() {
  const navigate = useNavigate();
  const [artist_name, setArtistName] = useState("");
  const [artist_description, setArtistDescription] = useState("");
  const [priority, setPriority] = useState(1);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('');

  const handleGenreChange = (e) => {
    setSelectedGenre(e.target.value);
  };

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const response = await allAPiServicesCall.getGenres({}, getAuthConfig());
        setGenres(response.data);
      } catch (error) {
        console.error('Error fetching genres:', error);
      }
    };

    fetchGenres();
  }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0] || null;
    setImageFile(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!artist_name) return toast.error("Artist name is required");

    setSubmitting(true);
    try {
      let fileToUpload = imageFile;
      if (imageFile) {
        fileToUpload = await imageCompression(imageFile, {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 800,
          useWebWorker: true,
        });
      }

      const form = new FormData();
      form.append("artist_name", artist_name.trim());
      form.append("slug", slugify(artist_name, { lower: true, strict: true }));
      if (selectedGenre) form.append("genres", selectedGenre);
      if (artist_description) form.append("artist_description", artist_description);
      form.append("priority", String(priority));
      if (fileToUpload) form.append("image", fileToUpload);

      const { data } = await allAPiServicesCall.createArtist(form, getAuthConfig());
      if (data?.success) {
        toast.success("Artist created");
        navigate("/artists");
      } else {
        toast.error(data?.message || "Failed to create artist");
      }
    } catch (err) {
      toast.error("Error creating artist");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CommonLayout>
      <div className="add-artist-page">
        <div className="add-artist-card">
          {/* Card Header */}
          <div className="add-artist-header">
            <h2>
              <FaUser /> Add New Artist
            </h2>
            <p>Create a new artist profile for the music library</p>
          </div>

          {/* Card Body */}
          <div className="add-artist-body">
            <form onSubmit={onSubmit}>
              {/* Artist Name */}
              <div className="add-artist-form-group">
                <label>
                  Artist Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="add-artist-input"
                  placeholder="Enter artist name"
                  value={artist_name}
                  onChange={(e) => setArtistName(e.target.value)}
                  required
                />
              </div>

              {/* Genre */}
              <div className="add-artist-form-group">
                <label>
                  Genre <span className="required">*</span>
                </label>
                <select
                  className="add-artist-select"
                  value={selectedGenre}
                  onChange={handleGenreChange}
                  required
                >
                  <option value="">Select Genre</option>
                  {genres.map((genre) => (
                    <option key={genre._id} value={genre.genreName}>
                      {genre.genreName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div className="add-artist-form-group">
                <label>Artist Description</label>
                <textarea
                  className="add-artist-textarea"
                  placeholder="Write a brief description about the artist..."
                  rows={4}
                  value={artist_description}
                  onChange={(e) => setArtistDescription(e.target.value)}
                />
                <p className="form-hint">Optional: Add biography, achievements, or other details</p>
              </div>

              {/* Image Upload */}
              <div className="add-artist-form-group">
                <label>Artist Image</label>
                <div className={`add-artist-image-upload ${imagePreview ? 'has-image' : ''}`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFile}
                  />
                  {!imagePreview ? (
                    <>
                      <FaCloudUploadAlt className="add-artist-upload-icon" />
                      <p className="add-artist-upload-text">
                        Click or drag image to upload
                      </p>
                      <p className="add-artist-upload-hint">
                        PNG, JPG, WEBP up to 5MB
                      </p>
                    </>
                  ) : (
                    <div className="add-artist-image-preview">
                      <img src={imagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={removeImage}
                      >
                        <FaTimes size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Section */}
              <div className="add-artist-submit-section">
                <button
                  type="button"
                  className="add-artist-btn add-artist-btn-secondary"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="add-artist-btn add-artist-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="add-artist-spinner"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave /> Save Artist
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </CommonLayout>
  );
}
